/**
 * Contratos del Despacho — el canal del directivo (ADR-11/ADR-13). El
 * actor NUNCA viaja en el cuerpo: sale de la sesión de dispositivo
 * (Bearer) que la API emite al canjear un enlace de acceso. Fechas ISO.
 * Los nombres visibles van en español (Aquila habla español); los
 * identificadores, en inglés.
 */
import { z } from "zod";
import { ReadAttachment, TurnAttachment } from "./attachments.js";

export const RoleProfileValue = z.enum(["CEO", "CFO", "Production", "Commercial"]);
export const Flavor = z.enum(["aquila"]);

/** Quién soy y en qué casa estoy — lo que la SPA pinta al entrar. */
export const DespachoMe = z.object({
  executive: z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    /** null = sin asignar (solo alcanzable en un sabor sin preset, ADR-12). */
    roleProfile: RoleProfileValue.nullable(),
    clientAdmin: z.boolean(),
    status: z.enum(["invited", "active"]),
  }),
  engagement: z.object({
    id: z.string(),
    companyName: z.string(),
    sector: z.string().nullable(),
    /** Sabor persistido en la casa al crearla (ADR-12/13): manda sobre el de build. */
    flavor: Flavor.nullable(),
  }),
  session: z.object({ id: z.string(), label: z.string(), createdAt: z.string().datetime() }),
});

/** Pedir enlace: correo conocido → enviado; desconocido → sin acceso (coste aceptado, ADR-13). */
export const LinkRequest = z.object({ email: z.string().email() });
export const LinkOutcome = z.object({ outcome: z.enum(["sent", "unknown"]) });

/** Alta de la fundadora (ADR-1): crea casa + fundadora y envía el mismo enlace.
 *  Si el correo ya tiene casa, se envía el enlace de ENTRADA y `existing` lo dice. */
export const SignupRequest = z.object({
  email: z.string().email(),
  name: z.string().min(1),
  companyName: z.string().min(1),
  /** Atributo estructural de la casa (ADR-1); la pantalla de alta lo pide. */
  sector: z.string().min(1).optional(),
});
export const SignupOutcome = z.object({ outcome: z.literal("sent"), existing: z.boolean() });

/** Canjear enlace → sesión de dispositivo. Errores: 404 invalid · 410 expired · 410 used;
 *  los 410 traen `purpose: entry|invitation` para contar la vida que tocaba. */
export const RedeemRequest = z.object({
  token: z.string().min(1),
  /** Etiqueta legible del aparato («Safari en Mac»); la SPA la deduce del UA. */
  device: z.string().min(1).max(120).optional(),
});
export const RedeemOutcome = z.object({ session: z.string(), me: DespachoMe });

/** Invitar (Client Admin): Executive `invited` + enlace de vida larga.
 *  Errores: 403 admin · 403 `TEST_HOUSE_INVITE_DENIED` · 400 invalid · 409 exists. */
export const InviteRequest = z.object({ email: z.string().email(), name: z.string().min(1).optional() });
export const InviteOutcome = z.object({ executiveId: z.string(), status: z.literal("invited") });
/** En una casa de prueba de un socio solo se invita a quien está en la lista del despliegue (ADR-17 pt 1, §5). */
export const TEST_HOUSE_INVITE_DENIED = "test-house-partners-only" as const;

/** Sesiones de dispositivo del propio Executive (§5, OQ-12 cerrado en ADR-13). */
export const DeviceSessionRow = z.object({
  id: z.string(),
  label: z.string(),
  createdAt: z.string().datetime(),
  lastSeenAt: z.string().datetime(),
  current: z.boolean(),
});
export const DeviceSessions = z.array(DeviceSessionRow);

// ── Las cifras del turno sobre el cable (ADR-18 pt 7) ─────────────────
// Lo que la pantalla necesita para enseñar de dónde sale cada cifra, ya
// resuelto: no tiene que seguir ids de llamada ni conocer la forma de la
// traza del dominio. Todo es aditivo: una traza vieja no lo lleva.

/** Periodo o fecha efectivamente servidos. */
export const FigurePeriodWire = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("period"), start: z.string(), end: z.string() }),
  z.object({ kind: z.literal("date"), date: z.string() }),
]);
export type FigurePeriodWire = z.infer<typeof FigurePeriodWire>;

/** Un documento de cifras en la procedencia de una cifra (ADR-22 pt 10). */
export const DocumentProvenanceWire = z.object({
  /** Nombre del fichero subido («ventas-sep.xlsx»). */
  name: z.string(),
  /** Hash corto del fichero (12 primeros caracteres): lo que lo distingue de otra subida con el mismo nombre. */
  hash: z.string(),
  /** Fecha de corte, `YYYY-MM-DD`. */
  cutOffDate: z.string(),
  /** Título de la tabla del documento. */
  table: z.string(),
  /** Versión del concepto que declara la cabecera. */
  conceptVersion: z.number().int(),
});
export type DocumentProvenanceWire = z.infer<typeof DocumentProvenanceWire>;

/** Procedencia de una cifra servida: fuente, origen presentable, receta (o entrada de cabecera) y, en `fixture`, fichero y fecha. */
export const FigureProvenanceWire = z.object({
  sourceType: z.string(),
  origin: z.string(),
  mapping: z.object({
    id: z.string(),
    version: z.number().int(),
    /** ADITIVO (M6a, PR 3b): la definición que implementa la receta o, en una foto, la de su cabecera (texto técnico, no para el directivo). */
    implements: z.string().optional(),
  }),
  file: z.object({ path: z.string(), date: z.string() }).nullable(),
  /** ADITIVO (M6a, PR 3b): la cifra sale de la foto fijada de una casa de prueba (ADR-17), no de un dato vivo. */
  snapshot: z.boolean().optional(),
  /**
   * ADITIVO (ADR-22 pt 10): la cifra sale de documentos de cifras de la
   * casa — cada uno que aportó cubos, con su fecha de corte y su tabla —.
   * La fecha del dato es la más antigua de ellas.
   */
  documents: z.array(DocumentProvenanceWire).optional(),
});
export type FigureProvenanceWire = z.infer<typeof FigureProvenanceWire>;

/**
 * De dónde sale una cifra, ya resuelto para pintar:
 * - `query`: un `query_concept` de este turno, con su resultado.
 * - `compute`: una operación de este turno; sus operandos, resueltos igual
 *   (la cadena entera hasta las consultas).
 * - `literal`: un valor fijo de un `compute`: en `percent_of` (ADR-18
 *   pt 4, rev. 2026-10-28), el porcentaje que dijo el directivo; desde v2
 *   (rev. 2026-10-29), cualquier cifra que dijo el directivo, en cualquier
 *   operación, con `said` y `source: "house"` — dato de la casa —, o un
 *   umbral del método en una comparación (`source: "skill"`).
 * - `recalled`: una cifra ya dada en el episodio; su traza es la del Message.
 * - `house`: dato de la casa (lo dijo el directivo o está en su memoria).
 * - `skill`: un umbral del método curado.
 * - `unresolved`: la llamada citada no está en la traza o no se ejecutó.
 */
export type FigureOrigin =
  | {
      kind: "query";
      callId: string;
      concept: string;
      /** El nombre presentable del concepto («Ventas»): el del catálogo; null solo si el concepto no está en él. */
      conceptLabel: string | null;
      conceptVersion: number;
      status: string;
      period: FigurePeriodWire | null;
      dataDate: string | null;
      provenance: FigureProvenanceWire | null;
      /** ADITIVO (PR 3b): el valor servido si es una sola fila (null si son varias), con su unidad y moneda. */
      value?: number | null | undefined;
      unit?: string | undefined;
      currency?: string | null | undefined;
      /** ADITIVO (PR 3b): qué mide, en llano, SOLO si la fuente declara una desviación sobre la definición del catálogo (ADR-17 pt 7); null si no. */
      measures?: string | null | undefined;
    }
  | {
      kind: "compute";
      callId: string;
      op: string;
      status: string;
      operands: FigureOrigin[];
      /** ADITIVO (PR 3b): el resultado si es una sola fila, con su unidad. */
      value?: number | null | undefined;
      unit?: string | undefined;
      currency?: string | null | undefined;
    }
  | {
      kind: "literal";
      value: number;
      /** ADITIVO (2026-10-28): la cifra tal como la dijo el directivo («6 %», «28 millones»): dato de la casa. */
      said?: string | undefined;
      /**
       * ADITIVO (2026-10-29, compute v2): de dónde sale el literal — `house`,
       * lo dijo el directivo («lo que me contaste»); `skill`, un umbral del
       * método (solo en una comparación) —, y su unidad y moneda. Ausentes en
       * una traza v1.
       */
      source?: "house" | "skill" | undefined;
      unit?: string | undefined;
      currency?: string | undefined;
    }
  | { kind: "recalled"; messageId: string }
  | { kind: "house" }
  | { kind: "skill" }
  | { kind: "unresolved"; callId: string };
export const FigureOrigin: z.ZodType<FigureOrigin> = z.lazy(() =>
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("query"),
      callId: z.string(),
      concept: z.string(),
      conceptLabel: z.string().nullable(),
      conceptVersion: z.number().int(),
      status: z.string(),
      period: FigurePeriodWire.nullable(),
      dataDate: z.string().nullable(),
      provenance: FigureProvenanceWire.nullable(),
      value: z.number().nullable().optional(),
      unit: z.string().optional(),
      currency: z.string().nullable().optional(),
      measures: z.string().nullable().optional(),
    }),
    z.object({
      kind: z.literal("compute"),
      callId: z.string(),
      op: z.string(),
      status: z.string(),
      operands: z.array(FigureOrigin),
      value: z.number().nullable().optional(),
      unit: z.string().optional(),
      currency: z.string().nullable().optional(),
    }),
    z.object({
      kind: z.literal("literal"),
      value: z.number(),
      said: z.string().optional(),
      source: z.enum(["house", "skill"]).optional(),
      unit: z.string().optional(),
      currency: z.string().optional(),
    }),
    z.object({ kind: z.literal("recalled"), messageId: z.string() }),
    z.object({ kind: z.literal("house") }),
    z.object({ kind: z.literal("skill") }),
    z.object({ kind: z.literal("unresolved"), callId: z.string() }),
  ]),
);

/** Una cifra tal como quedó escrita en la respuesta, con su valor y su origen. */
export const CitedFigureWire = z.object({ shown: z.string(), value: z.number(), origin: FigureOrigin });
export type CitedFigureWire = z.infer<typeof CitedFigureWire>;

/** Una llamada a herramienta del turno, resumida: qué se pidió, cómo acabó y qué dijo la conexión. */
export const FigureCallWire = z.object({
  id: z.string(),
  tool: z.string(),
  /** El estado del resultado (`ok`, `period_not_covered`…) o `invalid` si la petición no llegó a ejecutarse. */
  status: z.string(),
  message: z.string().nullable(),
});
export type FigureCallWire = z.infer<typeof FigureCallWire>;

/** Las cifras de UN turno: cada cifra citada con su origen, las llamadas y el resultado del comprobador. */
export const TurnFigures = z.object({
  cited: z.array(CitedFigureWire),
  calls: z.array(FigureCallWire),
  check: z.object({ outcome: z.enum(["passed", "regenerated", "degraded"]) }),
});
export type TurnFigures = z.infer<typeof TurnFigures>;

// ── La fuente de cifras de MI casa (§5, ADR-17 pt 7, R-8) ─────────────
// Lo que el flanco «Sus fuentes» enseña: ninguna, datos de demostración o
// la foto fijada de una casa de prueba, con su cabecera vigente. NUNCA una
// cifra de las tablas: solo de qué fecha es, qué cubre, qué mide y con qué
// consentimiento.

/** Lo que la cabecera de la foto dice de un concepto. */
export const SnapshotConceptWire = z.object({
  concept: z.string(),
  /** El nombre presentable del catálogo («Ventas»). */
  label: z.string(),
  /** Lo que cubre: un rango de meses (un flujo) o una fecha (un saldo). */
  coverage: FigurePeriodWire,
  /** null = la definición del catálogo, sin desviación; si no, el texto de la desviación declarada. */
  definition: z.string().nullable(),
});
export type SnapshotConceptWire = z.infer<typeof SnapshotConceptWire>;

/** La cabecera VIGENTE de la foto fijada de mi casa (ADR-17 pt 7). Fechas `YYYY-MM-DD`. */
export const SnapshotHeaderWire = z.object({
  company: z.string().nullable(),
  cutOffDate: z.string(),
  extractedOn: z.string().nullable(),
  builtBy: z.string(),
  consentRef: z.string(),
  approvedBy: z.string().nullable(),
  approvedOn: z.string().nullable(),
  concepts: z.array(SnapshotConceptWire),
});
export type SnapshotHeaderWire = z.infer<typeof SnapshotHeaderWire>;

/** Lo que la cabecera de un documento de cifras dice de un concepto (ADR-22 pt 8). */
export const FiguresDocumentConceptWire = SnapshotConceptWire.extend({
  version: z.number().int(),
  /** La versión que la tabla implementa se retiró del catálogo: deja de servirse; la casa sube una hoja nueva (pt 6). */
  versionRetired: z.literal(true).optional(),
});
export type FiguresDocumentConceptWire = z.infer<typeof FiguresDocumentConceptWire>;

export const FIGURES_DOCUMENT_STATES = ["draft", "fixed", "discarded", "retired"] as const;

/**
 * Un documento de cifras de mi casa en la lista de «Sus fuentes» (ADR-22
 * pt 1 y 8): cabecera y estado, nunca una cifra ni una fila. Fechas
 * `YYYY-MM-DD` (corte, visto bueno) o ISO 8601 (los actos).
 */
export const FiguresDocumentWire = z.object({
  id: z.string(),
  state: z.enum(FIGURES_DOCUMENT_STATES),
  fileName: z.string(),
  fileSize: z.number().int(),
  /** Hash corto (12 caracteres). */
  hash: z.string(),
  uploadedAt: z.string(),
  /** Lo construyó Skein por el camino asistido (pt 4), con consentimiento. */
  assisted: z.boolean(),
  cutOffDate: z.string(),
  approvedOn: z.string().nullable(),
  fixedAt: z.string().nullable(),
  discardedAt: z.string().nullable(),
  /** Quién lo pasó a descartado: la casa, la caducidad, una subida nueva o el miembro nombrado del camino asistido (`member`). */
  discardedBy: z.enum(["client-admin", "expiry", "replaced", "member"]).nullable(),
  retiredAt: z.string().nullable(),
  /** Lo que sirve, por concepto: solo en borrador y fijado (en un rastro ya no hay tablas). */
  concepts: z.array(FiguresDocumentConceptWire),
});
export type FiguresDocumentWire = z.infer<typeof FiguresDocumentWire>;

/** Los documentos de cifras de mi casa: si el asesor los sirve ahora (las puertas de una casa de prueba) y la lista. */
export const DespachoFiguresDocuments = z.object({
  served: z.boolean(),
  items: z.array(FiguresDocumentWire),
});
export type DespachoFiguresDocuments = z.infer<typeof DespachoFiguresDocuments>;

/**
 * `GET /despacho/sources`: qué fuente de cifras tiene la casa del actor
 * (R-5: solo la suya). `none` = ninguna (tampoco una casa de prueba sin foto
 * fijada); `demo` = casa de demostración, con el nombre del origen;
 * `snapshot` = casa de prueba con foto fijada — `served` dice si ahora mismo
 * el asesor responde con ella (sus puertas abiertas, ADR-17).
 */
export const DespachoSources = z.discriminatedUnion("kind", [
  /** ADITIVO (ADR-22): `documents`, en cualquiera de las tres, cuando la casa tiene documentos de cifras (en cualquier estado). */
  z.object({ kind: z.literal("none"), documents: DespachoFiguresDocuments.optional() }),
  z.object({ kind: z.literal("demo"), label: z.string(), documents: DespachoFiguresDocuments.optional() }),
  z.object({
    kind: z.literal("snapshot"),
    served: z.boolean(),
    header: SnapshotHeaderWire,
    /** ADITIVO (2026-10-26, ADR-17 pt 5): la casa tiene el modo personas — habla de personas, por alias. */
    people: z.literal(true).optional(),
    documents: DespachoFiguresDocuments.optional(),
  }),
]);
export type DespachoSources = z.infer<typeof DespachoSources>;

/**
 * Lo que el turno consumió del modelo (ADR-18 pt 7, revisión 2026-10-19):
 * llamadas y tokens tal como se guardaron, y el coste calculado al leer con
 * el precio de hoy — `null` si el modelo no tiene precio configurado o si
 * quien lee no valora (el Despacho no lo hace).
 */
export const TurnModel = z.object({
  name: z.string(),
  calls: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  cacheReadTokens: z.number().int(),
  cacheWriteTokens: z.number().int(),
  cost: z.object({ amount: z.number(), currency: z.string() }).nullable(),
});
export type TurnModel = z.infer<typeof TurnModel>;

/** Lo que formó UNA respuesta («no es magia»): método aplicado, criterios y memoria tenidos en cuenta. */
/**
 * Quién eligió el método del turno y entre cuáles (ADR-20 pt 9): la regla
 * sola (`lexical`), el modelo (`model`), el método del turno anterior
 * (`previous`), nadie (`none`, R-4) o la caída porque la llamada de
 * elección falló (`fallback`: el anterior o el primer candidato).
 * `previous`, el anterior si lo hubo.
 */
export const TurnSkillChoice = z.object({
  by: z.enum(["lexical", "model", "previous", "none", "fallback"]),
  candidates: z.array(z.string()),
  previous: z.string().optional(),
});
export type TurnSkillChoice = z.infer<typeof TurnSkillChoice>;

// ── La biblioteca de Skein en la traza (ADR-20 pts 4 y 5) ─────────────
// Nunca un nombre: la traza es una sola y el Executive la ve. La etiqueta
// de «Sus fuentes» la calcula el Despacho con esto (cinco casos del pt 5).

/** La posición de una ficha: `signed` («criterio de Skein, firmado el …», con su fecha si la hay) o `pending` («propuesta de Skein, pendiente de firma»). */
export const KnowledgeStandingWire = z.object({
  kind: z.enum(["signed", "pending"]),
  /** `house_criteria_date`, AAAA-MM-DD. */
  date: z.string().optional(),
});
export type KnowledgeStandingWire = z.infer<typeof KnowledgeStandingWire>;

/** Una ficha tal como quedó en la traza: lista cerrada, sin nombres ni cuerpo. */
export const KnowledgeRefWire = z.object({
  slug: z.string(),
  /** Código del cuaderno (`C02`). */
  notebook: z.string(),
  notebookTitle: z.string(),
  title: z.string(),
  /** Clase de ficha (`definition`, `pattern`…). */
  kind: z.string(),
  summary: z.string(),
  bodyHash: z.string(),
  standing: KnowledgeStandingWire,
});
export type KnowledgeRefWire = z.infer<typeof KnowledgeRefWire>;

/** Una ficha OFRECIDA en el turno (el índice), en corto: la ficha entera va solo en las citadas. */
export const KnowledgeOfferedWire = z.object({
  slug: z.string(),
  notebook: z.string(),
  bodyHash: z.string(),
  standing: KnowledgeStandingWire,
});
export type KnowledgeOfferedWire = z.infer<typeof KnowledgeOfferedWire>;

/** Una cifra de la respuesta que respalda el cuerpo de una ficha citada: «de la biblioteca», nunca en la línea de cifras de la casa. */
export const KnowledgeFigureWire = z.object({ shown: z.string(), value: z.number(), slug: z.string() });
export type KnowledgeFigureWire = z.infer<typeof KnowledgeFigureWire>;

/**
 * La biblioteca de UN turno: ofrecidas (el índice), leídas con la
 * herramienta, citadas (el bloque `skein-fuentes`, filtrado); `error`, el
 * puerto falló («biblioteca no disponible en este turno»).
 */
export const TurnKnowledge = z.object({
  offered: z.array(KnowledgeOfferedWire),
  read: z.array(z.string()),
  cited: z.array(KnowledgeRefWire),
  truncated: z.boolean().optional(),
  error: z.boolean().optional(),
  figures: z.array(KnowledgeFigureWire).optional(),
});
export type TurnKnowledge = z.infer<typeof TurnKnowledge>;

/**
 * `GET /despacho/conocimiento/:slug?message=<id>` (ADR-20 pt 5): la vista
 * pública de una ficha citada en un Message de MIS conversaciones. Sin
 * cuerpo, sin preguntas, sin firmas, sin `review_log`, sin nombres de
 * socios. `standing` es la de la traza; `changed`, el cuerpo cambió o la
 * ficha bajó de posición desde entonces; `retired`, ya no es legible.
 */
export const DespachoConocimiento = z.object({
  slug: z.string(),
  notebook: z.string(),
  notebookTitle: z.string(),
  kind: z.string(),
  title: z.string(),
  summary: z.string(),
  sources: z.array(z.object({ title: z.string(), author: z.string(), year: z.string(), url: z.string().nullable() })),
  standing: KnowledgeStandingWire,
  changed: z.boolean(),
  retired: z.boolean(),
});
export type DespachoConocimiento = z.infer<typeof DespachoConocimiento>;

/**
 * El puente que viajó a un turno (ADR-21 pt 2; aditivo a la traza, ADR-18
 * pt 7): el episodio de donde viene y su fecha (la de su último Message).
 * Nunca el texto: el texto se lee con la lista de episodios.
 */
export const TurnBridge = z.object({ conversationId: z.string(), date: z.string().datetime() });
export type TurnBridge = z.infer<typeof TurnBridge>;

/**
 * El recuerdo bajo demanda de un turno (ADR-21 pt 5; aditivo a la traza,
 * ADR-18 pt 7). Solo en los turnos que miraban al pasado (paso A):
 * - `asked: false` — el paso B dijo que no pedía recordar (o, con
 *   `error`, la llamada falló): no se buscó;
 * - `asked: true` — se buscó: la consulta, el rango (días `AAAA-MM-DD`) y
 *   los fragmentos POR REFERENCIA (episodio, fecha y autor; nunca el
 *   texto). Con `error`, la búsqueda falló.
 * «Recuerdo pedido sin fragmento» (`asked`, `fragments` vacío, sin
 * `error`) es el contador de OQ-20.
 */
export const TurnRecallFragment = z.object({
  conversationId: z.string(),
  date: z.string().datetime(),
  author: z.enum(["executive", "advisor", "bridge"]),
});
export type TurnRecallFragment = z.infer<typeof TurnRecallFragment>;
const RecallDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const TurnRecall = z.object({
  asked: z.boolean(),
  query: z.string().optional(),
  from: RecallDay.optional(),
  to: RecallDay.optional(),
  fragments: z.array(TurnRecallFragment).optional(),
  error: z.literal(true).optional(),
});
export type TurnRecall = z.infer<typeof TurnRecall>;

/** El contador de OQ-20 sobre una traza del cable: se pidió recordar, se buscó y no hubo fragmento. */
export const recallWithoutFragment = (trace: { recall?: TurnRecall | undefined } | null | undefined): boolean => {
  const r = trace?.recall;
  return r !== undefined && r.asked && r.error !== true && (r.fragments?.length ?? 0) === 0;
};

/**
 * La entrada por documento de un turno (ADR-21 pt 6; aditivo a la traza,
 * ADR-18 pt 7). Solo en el turno que trae un documento con la entrada por
 * documento encendida: su nombre, cuántos hechos dejó por confirmar y
 * cuántos de ellos corrigen uno confirmado; `dropped`, lo que la red del
 * dominio dejó fuera (repetido, cifra suelta, demasiado largo, más allá del
 * tope); `error`, la llamada falló y no se propuso nada. Nunca el texto.
 */
export const TurnDocument = z.object({
  name: z.string(),
  candidates: z.number().int().nonnegative(),
  superseding: z.number().int().nonnegative(),
  dropped: z
    .object({ repeated: z.number().int().nonnegative(), figures: z.number().int().nonnegative(), tooLong: z.number().int().nonnegative(), overMax: z.number().int().nonnegative() })
    .optional(),
  error: z.literal(true).optional(),
});
export type TurnDocument = z.infer<typeof TurnDocument>;

export const TurnTrace = z.object({
  skillSlug: z.string().nullable(),
  skillTitle: z.string().nullable(),
  /** ADITIVO (2026-10-27, ADR-20 pt 5): la versión publicada HOY del método (la traza guarda el slug, no la versión). */
  skillVersion: z.number().int().optional(),
  /** ADITIVO (2026-10-27): la frase del método para el directivo, del principio del Skill; null si no tiene ninguna que le hable a él. */
  skillSummary: z.string().nullable().optional(),
  confirmedFacts: z.number().int(),
  historyTurns: z.number().int(),
  decisionsKnown: z.number().int(),
  openFollowUps: z.number().int(),
  /** ADITIVO (M6a): solo en los turnos de una casa con cifras. */
  figures: TurnFigures.nullable().optional(),
  /** ADITIVO (2026-10-19): solo en los turnos cuya llamada al modelo dijo lo que consumió. */
  model: TurnModel.nullable().optional(),
  /**
   * ADITIVO (2026-10-26, ADR-20 pt 9): quién eligió el método y entre
   * cuáles (slugs). Solo en los turnos que tuvieron candidatos o método
   * anterior.
   */
  skillChoice: TurnSkillChoice.optional(),
  /** ADITIVO (2026-10-26, ADR-20 pt 4): la biblioteca del turno, si el asesor la tiene. */
  knowledge: TurnKnowledge.optional(),
  /** ADITIVO (2026-10-26, ADR-17 pt 5): la respuesta nombra a una persona por su alias (casa con modo personas). */
  people: z.literal(true).optional(),
  /** ADITIVO (2026-11-03, ADR-21 pt 2): el puente que viajó al turno, por referencia y con su fecha. Sin puente, no está. */
  bridge: TurnBridge.optional(),
  /** ADITIVO (2026-11-03, ADR-21 pt 5): el recuerdo bajo demanda del turno. Sin señal de mirar atrás, no está. */
  recall: TurnRecall.optional(),
  /** ADITIVO (2026-11-03, ADR-21 pt 6): la entrada por documento del turno. Sin documento, no está. */
  document: TurnDocument.optional(),
});
/** Un turno del episodio. `at` ISO. Los del asesor llevan su traza (los del skeleton viejo, null). */
export const DespachoTurn = z.object({
  /** ADITIVO (2026-10-26, ADR-20 pt 5): el id del Message; con él se abre la vista pública de una ficha citada. */
  id: z.string().optional(),
  role: z.enum(["user", "assistant"]),
  /** En un turno con documento, solo lo que escribiste (puede ir vacío): el texto del documento no vuelve al cliente. */
  content: z.string(),
  at: z.string().datetime(),
  trace: TurnTrace.nullable().optional(),
  /** ADITIVO (2026-10-06): el documento que adjuntaste en ese turno. */
  attachment: TurnAttachment.optional(),
});

/** Propuesta pendiente de la puerta del sí (R-2): un hecho esperando confirmación. */
export const PendingProposal = z.object({
  chainId: z.string(),
  factId: z.string(),
  statement: z.string(),
  scope: z.string(),
  /** Corregir desde el chat: el enunciado del criterio confirmado que esto sustituiría (sí = relevo atómico). */
  replaces: z.string().optional(),
});

/**
 * El episodio en curso: el `open` del directivo o, si no tiene ninguno
 * (se cerró por inactividad), el último que tuvo; ninguno la primera vez
 * (UC-01). Con episodios (ADR-21), el próximo Message cae en el abierto o
 * abre otro: el cliente ya no decide el `conversationId`.
 */
export const CurrentConversation = z.object({
  conversationId: z.string().nullable(),
  turns: z.array(DespachoTurn),
  pending: PendingProposal.nullable(),
  /** ADITIVO (2026-11-03, ADR-21): `closed` si ese episodio ya se cerró — el próximo Message abre otro; null sin conversación. */
  state: z.enum(["open", "closed"]).nullable().optional(),
  /**
   * ADITIVO (2026-11-03, ADR-21 pt 1; G-94): si este despliegue tiene la
   * herramienta de pruebas «Conversación nueva» (`newEpisode`). En
   * producción, `false`: la app no enseña el botón.
   */
  testTools: z.boolean().optional(),
});

/**
 * El puente de un episodio cerrado, como lo ve su Executive (ADR-21 pt 2;
 * §5: solo él). `date` es la fecha del episodio (su último Message);
 * `from`/`to`, su tramo. Ni el modelo ni el consumo: eso es del Studio.
 */
export const EpisodeBridge = z.object({
  text: z.string(),
  date: z.string().datetime(),
  from: z.string().datetime(),
  to: z.string().datetime(),
});
export type EpisodeBridge = z.infer<typeof EpisodeBridge>;

/**
 * Un episodio del directivo (ADR-21): lo que la app necesita para pintar
 * el separador de fecha y cargar los anteriores. `legacy`, una
 * conversación de antes de ADR-21: cerrada y sin puente.
 */
export const DespachoEpisode = z.object({
  conversationId: z.string(),
  state: z.enum(["open", "closed"]),
  openedAt: z.string().datetime(),
  /** El último Message del episodio (su fecha). */
  lastMessageAt: z.string().datetime(),
  /** Null mientras está abierto. En los `legacy`, la de su último Message. */
  closedAt: z.string().datetime().nullable(),
  /** Messages guardados del episodio (los «sí»/«no» cortos no escriben turno). */
  turns: z.number().int(),
  /** Null si aún no se ha escrito, si el directivo no habló en él o si es `legacy`. */
  bridge: EpisodeBridge.nullable(),
  legacy: z.boolean().optional(),
});
export type DespachoEpisode = z.infer<typeof DespachoEpisode>;

/** `GET /despacho/episodios`: los episodios del directivo que llama, del más reciente al más viejo. */
export const DespachoEpisodes = z.object({ episodes: z.array(DespachoEpisode) });
export type DespachoEpisodes = z.infer<typeof DespachoEpisodes>;

/** `GET /despacho/episodios/:conversationId`: un episodio MÍO con sus turnos (mismo formato que `CurrentConversation.turns`). */
export const DespachoEpisodeTurns = z.object({ episode: DespachoEpisode, turns: z.array(DespachoTurn) });
export type DespachoEpisodeTurns = z.infer<typeof DespachoEpisodeTurns>;

/** El 404 de `newEpisode` cuando el despliegue no tiene herramientas de pruebas (producción). */
export const TEST_TOOLS_DISABLED = "test-tools";

/** La COLA del asesor (R-3: una petición activa a la vez; lo demás espera a que tú quieras):
 *  propuestas pendientes que NO son la activa + temas abiertos que abrió el asesor. */
export const QueueItem = z.object({
  kind: z.enum(["proposal", "followup"]),
  id: z.string(),
  /** proposal: chainId · followup: el propio id */
  ref: z.string(),
  statement: z.string(),
  since: z.string().datetime(),
});
export const Queue = z.object({ items: z.array(QueueItem), count: z.number().int() });

/** Primera vez (UC-01): el asesor abre él. Solo si el directivo no tiene turnos; si los hay → 409 `conflict`. */
export const OpenOutcome = z.object({ conversationId: z.string(), reply: z.string(), trace: TurnTrace.nullable().optional() });

/** Un mensaje lleva texto, un documento leído (`ATTACHMENT_ROUTES.read`) o las dos cosas. */
export const SendMessageRequest = z
  .object({
    conversationId: z.string().optional(),
    text: z.string(),
    attachment: ReadAttachment.optional(),
  })
  .refine((m) => m.text.trim().length > 0 || m.attachment !== undefined, { message: "Falta el texto del mensaje.", path: ["text"] });
/** Respuesta del asesor — calco de AdvisorReply sobre el cable + ids. */
export const SendMessageOutcome = z.object({
  conversationId: z.string(),
  reply: z.string(),
  proposal: PendingProposal.nullable().optional(),
  confirmed: z.object({ chainId: z.string(), factId: z.string(), statement: z.string() }).optional(),
  rejected: z.object({ chainId: z.string(), factId: z.string() }).optional(),
  skillSlug: z.string().optional(),
  /** ADITIVO (2026-10-26, ADR-20 pt 5): el id del Message del asesor. */
  messageId: z.string().optional(),
  trace: z
    .object({
      skillSlug: z.string().optional(),
      /** ADITIVO (tramo 3): el nombre curado del método, para no enseñar slugs. */
      skillTitle: z.string().optional(),
      /** ADITIVO (2026-10-27): versión publicada hoy y frase para el directivo, como en `TurnTrace`. */
      skillVersion: z.number().int().optional(),
      skillSummary: z.string().nullable().optional(),
      confirmedFacts: z.number().int(),
      historyTurns: z.number().int(),
      decisionsKnown: z.number().int(),
      openFollowUps: z.number().int(),
      /** ADITIVO (M6a): las cifras del turno, si la casa tiene cifras. */
      figures: TurnFigures.optional(),
      /** ADITIVO (2026-10-26, ADR-20 pt 9): quién eligió el método y entre cuáles. */
      skillChoice: TurnSkillChoice.optional(),
      /** ADITIVO (2026-10-26, ADR-20 pt 4): la biblioteca del turno. */
      knowledge: TurnKnowledge.optional(),
      /** ADITIVO (2026-10-26, ADR-17 pt 5): la respuesta nombra a una persona por su alias. */
      people: z.literal(true).optional(),
      /** ADITIVO (2026-11-03, ADR-21 pt 2): el puente que viajó al turno. */
      bridge: TurnBridge.optional(),
      /** ADITIVO (2026-11-03, ADR-21 pt 5): el recuerdo bajo demanda del turno. */
      recall: TurnRecall.optional(),
      /** ADITIVO (2026-11-03, ADR-21 pt 6): la entrada por documento del turno. */
      document: TurnDocument.optional(),
    })
    .optional(),
});

/**
 * La misma conversación en streaming (`messages/stream`): mismo cuerpo
 * (`SendMessageRequest`) y misma sesión; responde `text/event-stream`:
 *   event: progress · data: StreamProgress — en una casa con cifras, qué
 *                  está haciendo el asesor («Consultando ventas…»); el
 *                  texto llega ENTERO tras comprobarse, en un solo delta
 *   event: delta · data: StreamDelta   — trozos del texto VISIBLE
 *   event: done  · data: SendMessageOutcome — el mismo objeto que `messages`;
 *                  su `reply` MANDA sobre la suma de los deltas
 *   event: error · data: StreamError
 * Los errores previos al stream (sesión, cuerpo) siguen siendo HTTP 401/400.
 */
export const StreamDelta = z.object({ text: z.string() });
export type StreamDelta = z.infer<typeof StreamDelta>;
export const StreamProgress = z.object({ label: z.string() });
export type StreamProgress = z.infer<typeof StreamProgress>;
export const StreamError = z.object({ message: z.string(), code: z.string().optional() });
export type StreamError = z.infer<typeof StreamError>;
export const STREAM_EVENTS = { progress: "progress", delta: "delta", done: "done", error: "error" } as const;

/** Actos sobre un criterio (INV-ProfileFact-1/3): confirmar o rechazar lo propuesto;
 *  retirar lo confirmado; corregir = el viejo queda `superseded` y nace uno nuevo
 *  confirmado con enlace («antes decía…»), en un solo acto. `statement` solo con `correct`. */
export const FactAct = z.object({
  act: z.enum(["confirm", "reject", "retract", "correct"]),
  statement: z.string().min(1).optional(),
});

/** Cuántos hechos caben en un «Confirmar lo marcado» (el tope de candidatos de un documento, con holgura). */
export const CONFIRM_SELECTED_MAX = 100;
/**
 * «Confirmar lo marcado» (ADR-21 pt 6; INV-ProfileFact-2/4): el acto
 * `confirm-selected` sobre los hechos `proposed` que el directivo deja
 * marcados. Cada uno se confirma con su propio compare-and-swap; el bloque
 * NO es atómico. Lo no marcado sigue `proposed`.
 */
export const ConfirmSelectedRequest = z.object({
  facts: z.array(z.object({ chainId: z.string().min(1), factId: z.string().min(1) })).min(1).max(CONFIRM_SELECTED_MAX),
});
/**
 * Lo que pasó con cada hecho marcado: `confirmed`, o `skipped` con el
 * porqué — ya no estaba `proposed` (`not-proposed`), su cadena avanzó
 * entretanto (`conflict`), corregía una cabeza que ya no es la vigente
 * (`stale`), es de otro directivo (`scope`) o no existe en esta casa
 * (`not-found`).
 */
export const ConfirmSelectedOutcome = z.object({
  results: z.array(
    z.object({
      chainId: z.string(),
      factId: z.string(),
      outcome: z.enum(["confirmed", "skipped"]),
      reason: z.enum(["not-proposed", "conflict", "stale", "scope", "not-found"]).optional(),
    }),
  ),
});
export type ConfirmSelectedRequest = z.infer<typeof ConfirmSelectedRequest>;
export type ConfirmSelectedOutcome = z.infer<typeof ConfirmSelectedOutcome>;

// ── «Lo que sé»: la superficie del perfil (§5, ADR-3/5/6) ─────────────────

export const ProfileFactRow = z.object({
  id: z.string(),
  chainId: z.string(),
  statement: z.string(),
  scope: z.enum(["engagement", "executive"]),
  /** El hecho executive-scoped es mío o de otro (de otro no se ve: §5 lo filtra en la API). */
  mine: z.boolean(),
  /** ADITIVO (2026-11-03, ADR-21 pt 6): `document`, sacado de un dossier que adjuntaste. */
  origin: z.enum(["signup", "conversation", "correction", "document"]),
  state: z.enum(["proposed", "confirmed", "rejected", "superseded", "retracted"]),
  proposedAt: z.string().datetime(),
  resolvedAt: z.string().datetime().nullable(),
  /** id del hecho al que sustituye (INV-ProfileFact-3) — el rastro «antes decía…». */
  supersedes: z.string().nullable(),
  /** La cadena entera, del más nuevo al más viejo, para el rastro. */
  chainVersion: z.number().int(),
  /**
   * ADITIVO (2026-11-03, ADR-21 pt 6): solo en los de origen `document`, el
   * documento de donde salió — el Message que lo trajo (para agruparlos),
   * el nombre del fichero (null si ese turno ya no se encuentra) y cuándo.
   */
  fromDocument: z.object({ messageId: z.string(), name: z.string().nullable(), at: z.string().datetime() }).nullable().optional(),
});
export const DecisionReview = z.object({
  /** Cómo fue: salió bien · a medias · no salió. */
  outcome: z.enum(["worked", "mixed", "failed"]),
  note: z.string().nullable(),
  at: z.string().datetime(),
  by: z.enum(["me", "other"]),
});
export const DecisionRow = z.object({
  id: z.string(),
  statement: z.string(),
  at: z.string().datetime(),
  /** Quién la enunció: yo, otro directivo de la casa. */
  decidedBy: z.enum(["me", "other"]),
  /** §5 (ADITIVO, tramo 3): de la casa o privada mía — lo ajeno no llega aquí. */
  scope: z.enum(["engagement", "executive"]),
  /** INV-DecisionLogEntry-2: recorded → (reviewed | dropped); la revisión APPENDE su acta, el texto no se reescribe. */
  state: z.enum(["recorded", "reviewed", "dropped"]),
  review: DecisionReview.nullable(),
});
export const FollowUpItem = z.object({
  id: z.string(),
  statement: z.string(),
  at: z.string().datetime(),
  openedBy: z.enum(["advisor", "me", "other"]),
  /** §5 (ADITIVO, tramo 3): un pendiente nace privado de quien conversa. */
  scope: z.enum(["engagement", "executive"]),
  state: z.enum(["open", "resolved", "dropped"]),
});
/** Todo lo que el asesor sabe de la casa y de mí, ya gateado por §5 en la API. */
/** Corrección de un dato estructural de la casa (R-2): nada se edita en silencio, el rastro es visible. */
export const StructuralChange = z.object({
  field: z.enum(["companyName", "sector"]),
  before: z.string().nullable(),
  after: z.string().nullable(),
  by: z.enum(["me", "other"]),
  at: z.string().datetime(),
});
export const DespachoProfile = z.object({
  engagement: z.object({
    companyName: z.string(),
    sector: z.string().nullable(),
    createdAt: z.string().datetime(),
    /** El rastro «antes decía…» de lo estructural, del más nuevo al más viejo. */
    changes: z.array(StructuralChange),
  }),
  facts: z.array(ProfileFactRow),
  decisions: z.array(DecisionRow),
  followUps: z.array(FollowUpItem),
});
/**
 * La FICHA DE LA CASA (ADR-21 pt 6, I-14): vista derivada en prosa de los
 * hechos confirmados que el lector puede leer (§5), un bloque por grupo de
 * «Lo que sé». No es un registro: nunca se edita (se corrige el hecho) y
 * nunca viaja al asesor. `markdown` es la misma ficha como texto, para
 * copiarla o descargarla.
 */
export const HouseSheetBlock = z.object({
  group: z.string(),
  title: z.string(),
  /** La glosa de alcance («solo lo ves tú»); null si el grupo no la lleva. */
  note: z.string().nullable(),
  paragraphs: z.array(z.string()),
  /** Los ids de los hechos que pinta, en orden. */
  facts: z.array(z.string()),
});
export const HouseSheet = z.object({
  house: z.object({ companyName: z.string(), sector: z.string().nullable() }),
  generatedAt: z.string().datetime(),
  factCount: z.number().int().nonnegative(),
  blocks: z.array(HouseSheetBlock),
  markdown: z.string(),
});
export const DecisionAct = z.discriminatedUnion("act", [
  z.object({ act: z.literal("drop") }),
  z.object({ act: z.literal("review"), outcome: z.enum(["worked", "mixed", "failed"]), note: z.string().max(600).optional() }),
]);
export const FollowUpAct = z.object({ act: z.enum(["resolve", "drop"]) });

// ── Cuenta · Equipo (Client Admin) ────────────────────────────────────────

export const TeamMember = z.object({
  executiveId: z.string(),
  name: z.string(),
  email: z.string(),
  roleProfile: RoleProfileValue.nullable(),
  clientAdmin: z.boolean(),
  status: z.enum(["invited", "active"]),
  /** Solo para `invited`: hasta cuándo vale el último enlace enviado. */
  invitationExpiresAt: z.string().datetime().nullable(),
  me: z.boolean(),
});
export const Team = z.array(TeamMember);
/** Mi nombre (solo el propio). */
export const NameUpdate = z.object({ name: z.string().min(1).max(120) });
/** Lo estructural de la casa (Client Admin), con rastro R-2. Al menos un campo. */
export const EngagementUpdate = z.object({ companyName: z.string().min(1).max(160).optional(), sector: z.string().min(1).max(120).nullable().optional() });
/** Conceder o retirar el Client Admin (INV-Executive-3: nunca la última). */
export const AdminGrant = z.object({ grant: z.boolean() });

export type TurnTrace = z.infer<typeof TurnTrace>;
/** ADITIVO (tramo 3): el tipo del acto sobre una decisión (drop | review). */
export type DecisionAct = z.infer<typeof DecisionAct>;
export type QueueItem = z.infer<typeof QueueItem>;
export type Queue = z.infer<typeof Queue>;
export type OpenOutcome = z.infer<typeof OpenOutcome>;
export type DecisionReview = z.infer<typeof DecisionReview>;
export type StructuralChange = z.infer<typeof StructuralChange>;
export type ProfileFactRow = z.infer<typeof ProfileFactRow>;
export type DecisionRow = z.infer<typeof DecisionRow>;
export type FollowUpItem = z.infer<typeof FollowUpItem>;
export type DespachoProfile = z.infer<typeof DespachoProfile>;
export type HouseSheetBlock = z.infer<typeof HouseSheetBlock>;
export type HouseSheet = z.infer<typeof HouseSheet>;
export type TeamMember = z.infer<typeof TeamMember>;

export type DespachoMe = z.infer<typeof DespachoMe>;
export type LinkOutcome = z.infer<typeof LinkOutcome>;
export type SignupOutcome = z.infer<typeof SignupOutcome>;
export type RedeemOutcome = z.infer<typeof RedeemOutcome>;
export type InviteOutcome = z.infer<typeof InviteOutcome>;
export type DeviceSessionRow = z.infer<typeof DeviceSessionRow>;
export type DespachoTurn = z.infer<typeof DespachoTurn>;
export type PendingProposal = z.infer<typeof PendingProposal>;
export type CurrentConversation = z.infer<typeof CurrentConversation>;
export type SendMessageOutcome = z.infer<typeof SendMessageOutcome>;

/** Rutas del Despacho — el mapa único que comparten API y cliente. Todas bajo /despacho. */
export const DESPACHO_ROUTES = {
  // acceso (sin sesión)
  link: "/despacho/access/link",
  signup: "/despacho/access/signup",
  redeem: "/despacho/access/redeem",
  // con sesión
  me: "/despacho/me",
  logout: "/despacho/logout",
  sessions: "/despacho/sessions",
  session: (id: string) => `/despacho/sessions/${encodeURIComponent(id)}`,
  invite: "/despacho/invitations",
  conversation: "/despacho/conversation",
  /** La vista pública de una ficha citada en un Message mío (ADR-20 pt 5). */
  conocimiento: (slug: string, messageId: string) => `/despacho/conocimiento/${encodeURIComponent(slug)}?message=${encodeURIComponent(messageId)}`,
  conocimientoPattern: "/despacho/conocimiento/:slug",
  open: "/despacho/conversation/open",
  /**
   * Conversación nueva (2026-10-27): HERRAMIENTA DE PRUEBAS desde ADR-21
   * (G-94) — solo en despliegues con `testTools`; si no, 404
   * `TEST_TOOLS_DISABLED`. Cierra el episodio abierto con su puente, como
   * la inactividad, y abre otro; nada se borra.
   */
  newEpisode: "/despacho/conversation/new",
  /** Mis episodios (ADR-21), del más reciente al más viejo. */
  episodes: "/despacho/episodios",
  /** Un episodio mío con sus turnos. */
  episode: (conversationId: string) => `/despacho/episodios/${encodeURIComponent(conversationId)}`,
  episodePattern: "/despacho/episodios/:conversationId",
  queue: "/despacho/queue",
  /** La fuente de cifras de mi casa (ADITIVO, M6a PR 3b). */
  sources: "/despacho/sources",
  messages: "/despacho/messages",
  messagesStream: "/despacho/messages/stream",
  factAct: (chainId: string, factId: string) => `/despacho/facts/${encodeURIComponent(chainId)}/${encodeURIComponent(factId)}/act`,
  /** «Confirmar lo marcado» (ADR-21 pt 6): el acto `confirm-selected` sobre varios hechos propuestos. */
  factsConfirmSelected: "/despacho/facts/confirm-selected",
  followUpResolve: (id: string) => `/despacho/followups/${encodeURIComponent(id)}/resolve`,
  // «Lo que sé» y cuenta (tramo 2)
  profile: "/despacho/profile",
  /**
   * La ficha de la casa (ADR-21 pt 6): vista en prosa de lo confirmado que
   * puedo leer, generada al vuelo. Con `?formato=md`, el mismo texto como
   * Markdown para descargar (`ficha-de-la-casa.md`).
   */
  houseSheet: "/despacho/ficha-de-la-casa",
  houseSheetMarkdown: "/despacho/ficha-de-la-casa?formato=md",
  decisionAct: (id: string) => `/despacho/decisions/${encodeURIComponent(id)}/act`,
  followUpAct: (id: string) => `/despacho/followups/${encodeURIComponent(id)}/act`,
  name: "/despacho/me/name",
  engagement: "/despacho/engagement",
  team: "/despacho/team",
  teamResend: (executiveId: string) => `/despacho/team/${encodeURIComponent(executiveId)}/resend`,
  teamAdmin: (executiveId: string) => `/despacho/team/${encodeURIComponent(executiveId)}/admin`,
} as const;
